// eslint-disable-next-line no-unused-vars
import React from 'react';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import { oneOf, string } from 'prop-types';

const StyledLinkButton = styled.span`
  display: inline-flex;
  max-width: 100%;

  &.fullWidth {
    display: flex;
    width: 100%;
  }

  .linkButton {
    align-items: center;
    box-sizing: border-box;
    display: inline-flex;
    font-family: inherit;
    font-weight: 600;
    justify-content: center;
    line-height: 1.2;
    max-width: 100%;
    text-align: center;
    text-decoration: none;
  }

  &.fullWidth .linkButton {
    display: flex;
    width: 100%;
  }

  .linkButton:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }
`;

const isExternalLink = href => /^(?:[a-z][a-z\d+.-]*:|\/\/)/i.test(href);
const normalizeAnchorId = value => value.trim().replace(/^#+/, '').replace(/\s+/g, '-');

const LinkButtonComponent = ({
  label,
  linkType,
  href,
  sectionAnchor,
  linkTarget,
  backgroundColor,
  textColor,
  fontSize,
  paddingVertical,
  paddingHorizontal,
  marginTop,
  marginRight,
  marginBottom,
  marginLeft,
  borderRadius,
  width,
}) => {
  const scrollToSection = linkType === 'section';
  const anchorId = normalizeAnchorId(sectionAnchor || '');
  const linkProps = {
    className: 'linkButton',
    target: scrollToSection ? undefined : linkTarget,
    rel: !scrollToSection && linkTarget === '_blank' ? 'noopener noreferrer' : undefined,
    style: {
      backgroundColor,
      borderRadius,
      color: textColor,
      fontSize,
      padding: `${paddingVertical} ${paddingHorizontal}`,
    },
  };
  const handleSectionClick = event => {
    event.preventDefault();
    if (!anchorId) return;

    document.getElementById(anchorId)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };
  const renderLink = () => {
    if (scrollToSection) {
      return (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <a href={ `#${anchorId}` } onClick={ handleSectionClick } { ...linkProps }>{label}</a>
      );
    }

    if (isExternalLink(href)) {
      return (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <a href={ href } { ...linkProps }>{label}</a>
      );
    }

    return (
      // eslint-disable-next-line react/jsx-props-no-spreading
      <Link to={ href } { ...linkProps }>{label}</Link>
    );
  };

  return (
    <StyledLinkButton
      className={ width === 'full' ? 'fullWidth' : '' }
      style={ {
        marginTop,
        marginRight,
        marginBottom,
        marginLeft,
      } }
    >
      {renderLink()}
    </StyledLinkButton>
  );
};

LinkButtonComponent.propTypes = {
  label: string,
  linkType: oneOf(['url', 'section']),
  href: string,
  sectionAnchor: string,
  linkTarget: oneOf(['_self', '_blank']),
  backgroundColor: string,
  textColor: string,
  fontSize: string,
  paddingVertical: string,
  paddingHorizontal: string,
  marginTop: string,
  marginRight: string,
  marginBottom: string,
  marginLeft: string,
  borderRadius: string,
  width: oneOf(['auto', 'full']),
};

const LinkButton = {
  label: 'Link Button',
  fields: {
    label: { type: 'text', label: 'Button label' },
    href: { type: 'text', label: 'Link URL' },
    linkType: {
      type: 'radio',
      label: 'Action',
      options: [
        { label: 'Open link', value: 'url' },
        { label: 'Scroll to section', value: 'section' },
      ],
    },
    sectionAnchor: { type: 'text', label: 'Section anchor' },
    linkTarget: {
      type: 'radio',
      label: 'Open link in',
      options: [
        { label: 'Same tab', value: '_self' },
        { label: 'New tab', value: '_blank' },
      ],
    },
    backgroundColor: { type: 'text', label: 'Background color' },
    textColor: { type: 'text', label: 'Text color' },
    fontSize: { type: 'text', label: 'Font size' },
    paddingVertical: { type: 'text', label: 'Vertical padding' },
    paddingHorizontal: { type: 'text', label: 'Horizontal padding' },
    marginTop: { type: 'text', label: 'Margin top' },
    marginRight: { type: 'text', label: 'Margin right' },
    marginBottom: { type: 'text', label: 'Margin bottom' },
    marginLeft: { type: 'text', label: 'Margin left' },
    borderRadius: { type: 'text', label: 'Corner radius' },
    width: {
      type: 'radio',
      label: 'Width',
      options: [
        { label: 'Fit content', value: 'auto' },
        { label: 'Full width', value: 'full' },
      ],
    },
  },
  defaultProps: {
    label: 'Open link',
    linkType: 'url',
    href: '/',
    sectionAnchor: '',
    linkTarget: '_self',
    backgroundColor: '#E21C1C',
    textColor: '#ffffff',
    fontSize: '14px',
    paddingVertical: '8px',
    paddingHorizontal: '32px',
    marginTop: '0',
    marginRight: '0',
    marginBottom: '0',
    marginLeft: '0',
    borderRadius: '2px',
    width: 'auto',
  },
  // eslint-disable-next-line react/jsx-props-no-spreading
  render: props => <LinkButtonComponent { ...props } />,
};

// eslint-disable-next-line import/prefer-default-export
export { LinkButton };
